import { useState } from "react";
import { faKey, faUserPen } from "@fortawesome/free-solid-svg-icons";
import api, { getData } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { Button, Field, PageHeader } from "../components/UI";
import { formatDate, initials } from "../utils/format";

export default function ProfilePage() {
  const { admin, setAdmin } = useAuth();
  const toast = useToast();
  const [profile, setProfile] = useState({
    name: admin.name,
    email: admin.email,
  });
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [busy, setBusy] = useState("");
  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy("profile");
    try {
      const data = getData(await api.patch("/auth/profile", profile));
      setAdmin(data.admin);
      toast.show("Profile updated.");
    } catch (err) {
      toast.show(err.message, "error");
    } finally {
      setBusy("");
    }
  };
  const savePassword = async (e) => {
    e.preventDefault();
    if (passwords.newPassword.length < 12)
      return toast.show(
        "New password must be at least 12 characters.",
        "error",
      );
    if (passwords.newPassword !== passwords.confirmPassword)
      return toast.show("New passwords do not match.", "error");
    setBusy("password");
    try {
      await api.patch("/auth/password", {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      toast.show("Password updated.");
    } catch (err) {
      toast.show(err.message, "error");
    } finally {
      setBusy("");
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="Account security"
        title="Admin profile"
        description="Keep your administrator identity and credentials current."
      />
      <section className="profile-grid">
        <aside className="panel profile-card">
          <div className="profile-large-avatar">{initials(admin.name)}</div>
          <h2>{admin.name}</h2>
          <p>{admin.email}</p>
          <span className="role-chip">{admin.role}</span>
          <dl>
            <div>
              <dt>Account status</dt>
              <dd>Active</dd>
            </div>
            <div>
              <dt>Last login</dt>
              <dd>{formatDate(admin.lastLogin)}</dd>
            </div>
          </dl>
        </aside>
        <div className="profile-forms">
          <form className="panel form-section" onSubmit={saveProfile}>
            <header>
              <span className="section-number">01</span>
              <div>
                <h2>Profile details</h2>
                <p>Used across audit and import records.</p>
              </div>
            </header>
            <div className="form-grid">
              <Field label="Full name">
                <input
                  required
                  value={profile.name}
                  onChange={(e) =>
                    setProfile({ ...profile, name: e.target.value })
                  }
                />
              </Field>
              <Field label="Email address">
                <input
                  type="email"
                  required
                  value={profile.email}
                  onChange={(e) =>
                    setProfile({ ...profile, email: e.target.value })
                  }
                />
              </Field>
            </div>
            <div className="form-actions">
              <Button icon={faUserPen} disabled={busy === "profile"}>
                {busy === "profile" ? "Saving…" : "Update profile"}
              </Button>
            </div>
          </form>
          <form className="panel form-section" onSubmit={savePassword}>
            <header>
              <span className="section-number">02</span>
              <div>
                <h2>Change password</h2>
                <p>Use at least 12 characters and a unique passphrase.</p>
              </div>
            </header>
            <div className="form-grid">
              <Field className="field-full" label="Current password">
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={passwords.currentPassword}
                  onChange={(e) =>
                    setPasswords({
                      ...passwords,
                      currentPassword: e.target.value,
                    })
                  }
                />
              </Field>
              <Field label="New password">
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength="12"
                  required
                  value={passwords.newPassword}
                  onChange={(e) =>
                    setPasswords({ ...passwords, newPassword: e.target.value })
                  }
                />
              </Field>
              <Field label="Confirm new password">
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength="12"
                  required
                  value={passwords.confirmPassword}
                  onChange={(e) =>
                    setPasswords({
                      ...passwords,
                      confirmPassword: e.target.value,
                    })
                  }
                />
              </Field>
            </div>
            <div className="form-actions">
              <Button icon={faKey} disabled={busy === "password"}>
                {busy === "password" ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
